'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

// AI 아바타 상담 음성 입력: 마이크 버튼 한 번 → 녹음 → 말이 멈추면 자동 종료 → 서버에서 받아쓰기 → onText
// - 말소리가 한 번 들린 뒤 SILENCE_MS 동안 조용하면 끝낸다 (정지 버튼으로 직접 끝내도 된다)
// - 처음 NO_VOICE_MS 동안 아무 소리도 없으면 받아쓰기 없이 끝내고 안내한다 (빈 녹음을 보내지 않음)
// - 최대 MAX_MS 까지만 녹음
export type VoiceState = 'idle' | 'requesting' | 'recording' | 'transcribing';

const SILENCE_MS = 1800;
const NO_VOICE_MS = 7000;
const MAX_MS = 30000;
const VOICE_LEVEL = 0.03; // 이 크기(RMS)를 넘으면 말소리로 본다

const IN_APP = /KAKAOTALK|NAVER\(inapp|Instagram|FBAN|FBAV|Line\//i;

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  for (const t of ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus']) {
    if (MediaRecorder.isTypeSupported?.(t)) return t;
  }
  return '';
}

export function useVoiceInput({
  url = '/api/avatar-chat/transcribe',
  onStart,
  onText,
}: {
  url?: string;
  onStart?: () => void;
  onText: (text: string) => void;
}) {
  const [supported, setSupported] = useState(false);
  // 첫 렌더(서버) 이후에만 버튼을 그린다. 지원하지 않는 환경에서도 버튼은 보이고, 누르면 이유를 안내한다
  const [ready, setReady] = useState(false);
  const [state, setState] = useState<VoiceState>('idle');
  const [level, setLevel] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const ctxRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);
  const discardRef = useRef(false);
  const onTextRef = useRef(onText);
  onTextRef.current = onText;

  useEffect(() => {
    setSupported(
      typeof window !== 'undefined' &&
        !!navigator.mediaDevices?.getUserMedia &&
        typeof MediaRecorder !== 'undefined' &&
        window.isSecureContext,
    );
    setReady(true);
  }, []);

  const cleanup = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    void ctxRef.current?.close().catch(() => undefined);
    ctxRef.current = null;
    setLevel(0);
  }, []);

  useEffect(() => () => {
    discardRef.current = true;
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    cleanup();
  }, [cleanup]);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
  }, []);

  const cancel = useCallback(() => {
    discardRef.current = true;
    stop();
  }, [stop]);

  const start = useCallback(async () => {
    if (state !== 'idle') return;
    setError(null);
    // 녹음을 못 하는 환경이면 버튼은 그대로 두고 이유를 알려 준다
    if (!window.isSecureContext) {
      setError('음성 입력은 보안 연결(https) 주소에서만 쓸 수 있어요. 글자로 입력해 주세요.');
      return;
    }
    if (!supported) {
      setError(
        IN_APP.test(navigator.userAgent)
          ? '이 앱 안에서는 음성 입력을 쓸 수 없어요. 오른쪽 위 메뉴에서 “다른 브라우저로 열기”를 눌러 주세요.'
          : '이 브라우저에서는 음성 입력을 쓸 수 없어요. 크롬이나 사파리에서 열어 주세요.',
      );
      return;
    }
    discardRef.current = false;
    onStart?.();

    // iOS 는 사용자가 누른 그 순간에 AudioContext 를 만들어야 한다
    const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    const ctx = AC ? new AC() : null;
    ctxRef.current = ctx;

    setState('requesting');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      cleanup();
      setState('idle');
      const name = (e as DOMException)?.name;
      if (name === 'NotFoundError' || name === 'OverconstrainedError') setError('마이크를 찾을 수 없어요. 글자로 입력해 주세요.');
      else if (IN_APP.test(navigator.userAgent))
        setError('이 앱 안에서는 마이크를 쓸 수 없어요. 오른쪽 위 메뉴에서 “다른 브라우저로 열기”를 눌러 주세요.');
      else setError('마이크 사용이 막혀 있어요. 주소창 옆 자물쇠(사이트 설정)에서 마이크를 허용해 주세요.');
      return;
    }
    streamRef.current = stream;

    const mimeType = pickMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorderRef.current = recorder;
    const chunks: Blob[] = [];
    let heardVoice = false;
    recorder.ondataavailable = (ev) => {
      if (ev.data.size > 0) chunks.push(ev.data);
    };
    recorder.onstop = async () => {
      cleanup();
      recorderRef.current = null;
      if (discardRef.current) {
        setState('idle');
        return;
      }
      if (!heardVoice) {
        setState('idle');
        setError('소리가 잘 들리지 않았어요. 마이크 버튼을 누르고 다시 말씀해 주세요.');
        return;
      }
      setState('transcribing');
      try {
        const blob = new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
        const form = new FormData();
        form.append('audio', blob);
        const res = await fetch(url, { method: 'POST', body: form });
        const json = await res.json().catch(() => null);
        if (!res.ok) throw new Error(json?.error ?? '');
        const text = String(json?.text ?? '').trim();
        if (!text) setError('말씀을 알아듣지 못했어요. 다시 한 번 말씀해 주세요.');
        else onTextRef.current(text);
      } catch {
        setError('음성 인식에 실패했어요. 다시 시도하시거나 글자로 입력해 주세요.');
      } finally {
        setState('idle');
      }
    };

    // 소리 크기 측정: 화면의 소리 막대 + 말이 끝났는지 판단
    let analyser: AnalyserNode | null = null;
    if (ctx) {
      try {
        if (ctx.state === 'suspended') await ctx.resume();
        analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        ctx.createMediaStreamSource(stream).connect(analyser);
      } catch {
        analyser = null;
      }
    }
    const buf = analyser ? new Float32Array(analyser.fftSize) : null;

    const t0 = Date.now();
    let lastVoice = t0;
    recorder.start(250);
    setSeconds(0);
    setState('recording');

    timerRef.current = window.setInterval(() => {
      const now = Date.now();
      setSeconds(Math.floor((now - t0) / 1000));
      if (analyser && buf) {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
        const rms = Math.sqrt(sum / buf.length);
        setLevel(Math.min(1, rms * 8));
        if (rms > VOICE_LEVEL) {
          heardVoice = true;
          lastVoice = now;
        }
        if (heardVoice && now - lastVoice > SILENCE_MS) stop();
        else if (!heardVoice && now - t0 > NO_VOICE_MS) stop();
      } else {
        // 소리 크기를 잴 수 없는 브라우저: 정지 버튼 또는 최대 시간으로만 끝낸다
        heardVoice = true;
      }
      if (now - t0 > MAX_MS) stop();
    }, 100);
  }, [state, supported, onStart, cleanup, stop, url]);

  return { ready, supported, state, level, seconds, error, setError, start, stop, cancel };
}
